import React from "react";
import { FiBox, FiClock, FiMapPin, FiSearch, FiTruck } from "react-icons/fi";
import { Link } from "react-router-dom";
import { formatPrice, siteConfig } from "../Datas/productData";
import "../css/delivery.scss";

function Delivery() {
  return (
    <main className="delivery-page">
      <header className="delivery-page__hero">
        <div>
          <p>Customer care</p>
          <h1>From our studio to your door.</h1>
          <span>
            Clear delivery options, careful packaging and tracking from the
            moment your order leaves us.
          </span>
        </div>
        <FiTruck aria-hidden="true" />
      </header>

      <section className="delivery-page__services" aria-labelledby="delivery-services-title">
        <div className="delivery-page__section-head">
          <p>Delivery options</p>
          <h2 id="delivery-services-title">Choose the pace that works for you.</h2>
        </div>
        <div className="delivery-page__service-grid">
          <article>
            <FiBox aria-hidden="true" />
            <p>Standard</p>
            <h3>3–5 business days</h3>
            <dl>
              <div>
                <dt>Orders under {formatPrice(siteConfig.shipping.freeThreshold)}</dt>
                <dd>{formatPrice(siteConfig.shipping.standard)}</dd>
              </div>
              <div>
                <dt>Orders from {formatPrice(siteConfig.shipping.freeThreshold)}</dt>
                <dd>Complimentary</dd>
              </div>
            </dl>
          </article>
          <article>
            <FiClock aria-hidden="true" />
            <p>Express</p>
            <h3>1–2 business days</h3>
            <dl>
              <div>
                <dt>Availability</dt>
                <dd>Shown at checkout</dd>
              </div>
              <div>
                <dt>Price</dt>
                <dd>Calculated by destination</dd>
              </div>
            </dl>
          </article>
          <article>
            <FiMapPin aria-hidden="true" />
            <p>International</p>
            <h3>Destination specific</h3>
            <dl>
              <div>
                <dt>Time and carrier</dt>
                <dd>Confirmed at checkout</dd>
              </div>
              <div>
                <dt>Duties and taxes</dt>
                <dd>Shown before payment where available</dd>
              </div>
            </dl>
          </article>
        </div>
        <p className="delivery-page__service-note">
          Delivery estimates begin after dispatch and may take longer during
          launches, public holidays or exceptional carrier disruption.
        </p>
      </section>

      <section className="delivery-page__journey" aria-labelledby="delivery-journey-title">
        <div className="delivery-page__journey-copy">
          <p className="delivery-page__eyebrow">Your order journey</p>
          <h2 id="delivery-journey-title">Packed with attention. Tracked all the way.</h2>
          <p>
            Every order is checked before it leaves our fulfilment partner.
            Hardware and leather are protected, accessories are gathered and the
            parcel is sealed for the journey ahead.
          </p>
        </div>
        <ol>
          <li>
            <span>01</span>
            <div>
              <h3>Order received</h3>
              <p>Your confirmation arrives by email once checkout is complete.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <h3>Prepared and checked</h3>
              <p>We prepare the item, complete quality checks and hand it to the carrier.</p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <h3>On its way</h3>
              <p>A dispatch email contains the tracking link and latest delivery estimate.</p>
            </div>
          </li>
          <li>
            <span>04</span>
            <div>
              <h3>Delivered</h3>
              <p>The carrier records delivery at your address or agreed safe location.</p>
            </div>
          </li>
        </ol>
      </section>

      <section
        className="delivery-page__tracking"
        id="tracking"
        aria-labelledby="tracking-title"
      >
        <div className="delivery-page__tracking-visual" aria-hidden="true">
          <FiSearch />
          <span>TRACK</span>
          <span>TRACE</span>
        </div>
        <div className="delivery-page__tracking-copy">
          <p className="delivery-page__eyebrow">Tracking</p>
          <h2 id="tracking-title">Know where your parcel is.</h2>
          <p>
            Open the carrier link in your dispatch email for live updates. It can
            take several hours after dispatch for the first scan to appear.
          </p>
          <p>
            If tracking has not changed for three business days, contact us with
            your order number and we will investigate with the carrier.
          </p>
          <Link to="/contact">Contact customer care <span aria-hidden="true">→</span></Link>
        </div>
      </section>

      <section className="delivery-page__notes" aria-labelledby="delivery-notes-title">
        <div className="delivery-page__section-head">
          <p>Before ordering</p>
          <h2 id="delivery-notes-title">Useful delivery details.</h2>
        </div>
        <div className="delivery-page__accordions">
          <details>
            <summary>Which destinations can I select?</summary>
            <p>The country selector and checkout show destinations currently served. A complete, deliverable street address is required; some carriers cannot deliver to PO boxes.</p>
          </details>
          <details>
            <summary>Will I pay duties or import taxes?</summary>
            <p>Checkout shows whether duties and taxes are included where that information is available. For other destinations, the carrier or local authority may collect charges before delivery.</p>
          </details>
          <details>
            <summary>Can I change my address after ordering?</summary>
            <p>Contact customer care immediately. Once fulfilment begins or the carrier has the parcel, a change may no longer be possible.</p>
          </details>
          <details>
            <summary>What if I miss the delivery?</summary>
            <p>The carrier will normally leave digital instructions for a new attempt, collection point or safe delivery option. Follow the tracking page before the parcel is returned.</p>
          </details>
          <details>
            <summary>What should I do if the parcel looks damaged?</summary>
            <p>Photograph the outer packaging before opening it, retain all packing materials and contact us promptly if the product is affected.</p>
          </details>
        </div>
      </section>

      <section className="delivery-page__cta">
        <p>Need another answer?</p>
        <h2>Our customer-care team is ready to help.</h2>
        <div>
          <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
          <Link to="/returns">Returns information</Link>
        </div>
      </section>
    </main>
  );
}

export default Delivery;
